import type { ButtonProps } from '@hover/blueprint';
import { Box, Center, Icon, motion, useTimeout } from '@hover/blueprint';
import { iMoreHorizontal } from '@hover/icons';
import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { getCustomizeMenuForImage } from 'src/modules/homeownerApp/components/Customize/menu';
import { LoadingTextEllipsis } from 'src/modules/homeownerApp/components/LoadingText';
import {
  useCustomizeHandler,
  useHomeownerAppContext,
} from 'src/modules/homeownerApp/hooks';
import { Button } from 'src/shared/components';
import {
  type AiDisplayImage,
  type Maybe,
  useAiGeneratedImageRegionsQuery,
} from 'src/shared/graphql/generated';
import { TranslationNamespace, useTranslate } from 'src/shared/hooks';
import { useTrackingMetadata } from 'src/shared/hooks/useSegmentHistory';
import type { OptionalTrackingProperties } from 'src/shared/hooks/useTracking';

import { useAvailableMaterialsRegions } from '../../Palette';

interface ProjectEditButtonProps extends ButtonProps {
  generatedImage: Maybe<AiDisplayImage>;
  projectId: string;
  triggerImpression: (
    optionalTrackingProperties: OptionalTrackingProperties,
  ) => void;
}

const MAX_ICONS = 4;
const OPACITY_TRANSITION = '1.25s';
const MAXWIDTH_TRANSITION = '1.5s';

const MotionCenter = motion(Center);

const ProjectEditButton = ({
  generatedImage,
  projectId,
  triggerImpression,
  ...props
}: ProjectEditButtonProps) => {
  const t = useTranslate(TranslationNamespace.HOMEOWNER_APP);

  const [allowAnimationTimeout, setAllowAnimationTimeout] = useState(false);
  const [loading, setLoading] = useState(true);
  const {
    metadata: { partner_id: partnerId },
  } = useTrackingMetadata();

  const { trackingData: contextTrackingData } = useHomeownerAppContext();

  const { onClick, tracking } = useCustomizeHandler({
    image: generatedImage,
  });

  const availableMaterialsRegions = useAvailableMaterialsRegions();

  const { data: regionsData, loading: loadingRegions } =
    useAiGeneratedImageRegionsQuery({
      skip: !generatedImage,
      variables: {
        id: String(generatedImage?.aiGeneratedImage.id),
      },
    });

  const customizeOptions = getCustomizeMenuForImage(
    t,
    generatedImage,
    regionsData?.aiGeneratedImage,
    availableMaterialsRegions,
  );

  useTimeout(() => {
    setAllowAnimationTimeout(true);
  }, 1000);

  useEffect(() => {
    if (allowAnimationTimeout) {
      setLoading(loadingRegions && (customizeOptions?.length ?? 0) === 0);
    }
  }, [loadingRegions, customizeOptions, allowAnimationTimeout]);

  const renderIcons = () => {
    if (!customizeOptions) return null;

    return (
      <>
        {customizeOptions.slice(0, MAX_ICONS - 1).map(item => {
          const { region, type } = item.category;

          return (
            <Icon icon={item.icon} key={`${type}-${region}`} marginLeft={200} />
          );
        })}
        {customizeOptions.length > MAX_ICONS && (
          <Icon icon={iMoreHorizontal} marginLeft={200} />
        )}
      </>
    );
  };

  const optionalTrackingProperties: OptionalTrackingProperties = useMemo(
    () => ({
      ...contextTrackingData,
      ...tracking,
      button_text: t('projects.edit.edit'),
      partner_id: partnerId,
      primary_cta: false,
      project_id: Number(projectId),
      space_type: generatedImage?.spaceType ?? undefined,
      track_id: 'edit-button-project-page',
      view_type: 'edit',
    }),
    [
      contextTrackingData,
      generatedImage?.spaceType,
      partnerId,
      projectId,
      t,
      tracking,
    ],
  );

  const trackImpression = useCallback(() => {
    triggerImpression(optionalTrackingProperties);
  }, [optionalTrackingProperties, triggerImpression]);

  return (
    <Button
      colorMode="dark"
      data-testid="project-edit-button"
      label={t('projects.edit.edit')}
      onClick={onClick}
      optionalTrackingProperties={optionalTrackingProperties}
      transition={{ width: '0.5s' }}
      variant="glass"
      {...props}
    >
      <MotionCenter onViewportEnter={trackImpression}>
        {t('projects.edit.edit')}
        {loading && <LoadingTextEllipsis ml={100} />}
        <Box
          ml={loading ? 0 : 200}
          sx={{
            maxWidth: loading ? '0' : '100dvw',
            opacity: 1,
            transition: `opacity ${OPACITY_TRANSITION}, max-width ${MAXWIDTH_TRANSITION}`,
          }}
        >
          {renderIcons()}
        </Box>
      </MotionCenter>
    </Button>
  );
};

export { ProjectEditButton };
